import { autoinject, bindable, bindingMode, TaskQueue } from 'aurelia-framework';
import { Uuid } from '../../../helpers/uuid';

/**
 * This is a WCAG AA-compliant replacement for select/option drop-downs.
 */
@autoinject
export class ListBox {
	/**
	 * Save a list of named combobox actions, for future readability.
	 */
	private readonly SelectActions = {
		Close: 0,
		CloseSelect: 1,
		First: 2,
		Last: 3,
		Next: 4,
		Open: 5,
		PageDown: 6,
		PageUp: 7,
		Previous: 8,
		Select: 9,
		Type: 10
	};

	id: string;
	readonly selectedOptionBoxId: string;
	readonly listMenuId: string;
	readonly optionIdBase: string;
	readonly uuid: string;

	private isInitizialized: boolean = false;

	/**
	 * The actual values which will be tied to the individual options.
	 */
	@bindable options: any[] = [];

	/**
	 * If needed, this is the name of the property on the option which will be used as the display string
	 * (e.g. given "name," the property "name" on the "person" option in will be displayed as the text for
	 * the option - i.e., "person.name" will be used).
	 */
	@bindable optionPropertyDisplayName: string;

	/**
	 * In lieu of giving the display property name, an array of separate strings can be displayed
	 * inside the list box indexed to its related-indexed option.
	 */
	@bindable descriptions: any[] = [];
	/**
	 * The default selected option (if given, otherwise ignored)
	 */
	@bindable defaultOption: any;

	@bindable({ defaultBindingMode: bindingMode.twoWay }) selectedOption: any;

	@bindable labelId: string;

	/**
	 * Allows for some delegate to be executed when the selected option is changed.
	 */
	@bindable selectedOptionChangedCallback = () => { };

	activeIndex: number = 0;
	defaultIndex: number = 0;
	isOpen: boolean = false;
	searchString: string = '';
	searchTimeout: any = null;
	ignoreBlur: boolean;

	constructor(
		readonly element: Element,
		private readonly taskQueue: TaskQueue) {
		this.id = this.element.id;
		if (!this.id) {
			this.id = 'u' + Uuid.next(); // in the case of a failure to pick up an id, we generate a uniqe id (which must start with a letter)
		}
		this.selectedOptionBoxId = this.id + '-selected-option-box';
		this.listMenuId = this.id + '-list-menu';
		this.optionIdBase = this.id + '-option-';
	}

	attached(): void {
		if (this.listMenuDiv && this.selectedOptionDiv && this.options?.length > 0) {
			this.init();
		}
	}

	init(): void {
		if (!this.isInitizialized && this.listMenuDiv && this.selectedOptionDiv) {
			// add event listeners
			this.listMenuDiv.addEventListener('blur', this.onListMenuBlur.bind(this));
			this.selectedOptionDiv.addEventListener('blur', this.onSelectedOptionBlur.bind(this));
			this.selectedOptionDiv.addEventListener('click', this.onComboClick.bind(this));
			this.selectedOptionDiv.addEventListener('keydown', this.onComboKeyDown.bind(this));

			this.refreshOptionDivs();
			this.setDefaultOption();

			this.isInitizialized = true;
		}
	}

	// #region Events

	onSelectedOptionBlur(event: any): void {
		// Prevent menu from closing when clicking on scrollbar
		if ((event?.relatedTarget?.id ?? '') === this.listMenuDiv.id) {
			return;
		}

		// do not do blur action if ignoreBlur flag has been set
		if (this.ignoreBlur) {
			this.ignoreBlur = false;
			return;
		}

		// select current option and close
		if (this.isOpen) {
			this.selectOption(this.activeIndex);
			this.updateMenuState(false, false);
		}
	}

	onListMenuBlur(): void {
		if (document.activeElement === (this.listMenuDiv as Element)) {
			return;
		}
		// do not do blur action if ignoreBlur flag has been set
		if (this.ignoreBlur) {
			this.ignoreBlur = false;
			return;
		}

		// select current option and close
		if (this.isOpen) {
			this.selectOption(this.activeIndex);
			this.updateMenuState(false, false);
		}
	}

	onComboClick(): void {
		this.updateMenuState(!this.isOpen, false);
	}

	onComboKeyDown(event: any): void {
		const { key } = event;
		const max = this.options.length - 1;

		const action = this.getActionFromKey(event, this.isOpen);

		switch (action) {
			case this.SelectActions.Last:
			case this.SelectActions.First:
				this.updateMenuState(true);
			// intentional fallthrough
			case this.SelectActions.Next:
			case this.SelectActions.Previous:
			case this.SelectActions.PageUp:
			case this.SelectActions.PageDown:
				event.preventDefault();
				return this.changeOption(
					this.getUpdatedIndex(this.activeIndex, max, action)
				);
			case this.SelectActions.CloseSelect:
				event.preventDefault();
				this.selectOption(this.activeIndex);
			// intentional fallthrough
			case this.SelectActions.Close:
				event.preventDefault();
				return this.updateMenuState(false);
			case this.SelectActions.Type:
				return this.onComboType(key);
			case this.SelectActions.Open:
				event.preventDefault();
				return this.updateMenuState(true);
		}
	}

	onComboType(letter: any): void {
		// open the listbox if it is closed
		this.updateMenuState(true);

		// find the index of the first matching option
		const searchString = this.getSearchString(letter);
		const searchIndex = this.getIndexByLetter(
			this.descriptions,
			searchString,
			this.activeIndex + 1
		);

		// if a match was found, go to it
		if (searchIndex >= 0) {
			this.changeOption(searchIndex);
		}
		// if no matches, clear the timeout and search string
		else {
			window.clearTimeout(this.searchTimeout);
			this.searchString = '';
		}
	}

	onOptionClick(index: number): void {
		this.changeOption(index);
		this.selectOption(index);
		this.updateMenuState(false);
	}

	onOptionMouseDown(): void {
		// Clicking an option will cause a blur event,
		// but we don't want to perform the default keyboard blur action
		this.ignoreBlur = true;
	}

	// #endregion

	// #region Option/Menu state

	changeOption(index: number): void {
		// update state
		this.activeIndex = index;
		const activeDiv = this.optionDivs[this.activeIndex];

		// update aria-activedescendant
		this.selectedOptionDiv.setAttribute('aria-activedescendant', activeDiv.id);

		[...this.optionDivs].forEach((optionDiv) => {
			optionDiv.classList.remove('combo-option-current');
		});
		activeDiv.classList.add('combo-option-current');

		// ensure the new option is in view
		if (this.isScrollable(this.listMenuDiv)) {
			this.maintainScrollVisibility(activeDiv, this.listMenuDiv);
		}
	}

	selectOption(index: number): void {
		// update state
		this.activeIndex = index;

		// update displayed value
		this.selectedOptionDiv.innerText = this.getOptionDescription(this.options[index], index);
		this.selectedOptionDiv.setAttribute('value', this.options[index]);
		this.selectedOption = this.options[index];

		[...this.optionDivs].forEach((optionDiv) => {
			optionDiv.setAttribute('aria-selected', 'false');
		});
		this.optionDivs[index].setAttribute('aria-selected', 'true');
	}

	updateMenuState(open: boolean, callFocus = true): void {
		if (this.isOpen === open) {
			return;
		}

		// update state
		this.isOpen = open;

		// update aria-expanded and styles
		this.selectedOptionDiv.setAttribute('aria-expanded', `${open}`);
		open ? this.listMenuDiv.classList.add('combo-open') : this.listMenuDiv.classList.remove('combo-open');

		// update activedescendant
		const activeID = open ? this.optionDivs[this.activeIndex].id : '';
		this.selectedOptionDiv.setAttribute('aria-activedescendant', activeID);

		// move focus back to the combobox, if needed
		if (callFocus) {
			this.selectedOptionDiv.focus();
		}
	}

	// #endregion

	// #region Helper functions

	getSearchString(char: any): string {
		// reset typing timeout and start new timeout
		// this allows us to make multiple-letter matches, like a native select
		if (typeof this.searchTimeout === 'number') {
			window.clearTimeout(this.searchTimeout);
		}

		this.searchTimeout = window.setTimeout(() => {
			this.searchString = '';
		}, 500);

		// add most recent letter to saved search string
		this.searchString += char;
		return this.searchString;
	}

	// filter an array of options against an input string
	// returns an array of options that begin with the filter string, case-independent
	filterOptions(options = [], filter: string, exclude = []): any[] {
		return options.filter((option) => {
			const matches = option.toString().toLowerCase().indexOf(filter.toLowerCase()) === 0;
			return matches && exclude.indexOf(option) < 0;
		});
	}

	// map a key press to an action
	getActionFromKey(event: any, menuOpen: boolean): number {
		const { key, altKey, ctrlKey, metaKey } = event;
		const openKeys = ['ArrowDown', 'ArrowUp', 'Enter', ' ']; // all keys that will do the default open action
		// handle opening when closed
		if (!menuOpen && (openKeys.indexOf(key) > -1)) {
			return this.SelectActions.Open;
		}

		// home and end move the selected option when open or closed
		if (key === 'Home') {
			return this.SelectActions.First;
		}
		if (key === 'End') {
			return this.SelectActions.Last;
		}

		// handle typing characters when open or closed
		if (
			key === 'Backspace' ||
			key === 'Clear' ||
			(key.length === 1 && key !== ' ' && !altKey && !ctrlKey && !metaKey)
		) {
			return this.SelectActions.Type;
		}

		// handle keys when open
		if (menuOpen) {
			if (key === 'ArrowUp' && altKey) {
				return this.SelectActions.CloseSelect;
			} else if (key === 'ArrowDown' && !altKey) {
				return this.SelectActions.Next;
			} else if (key === 'ArrowUp') {
				return this.SelectActions.Previous;
			} else if (key === 'PageUp') {
				return this.SelectActions.PageUp;
			} else if (key === 'PageDown') {
				return this.SelectActions.PageDown;
			} else if (key === 'Escape') {
				return this.SelectActions.Close;
			} else if (key === 'Enter' || key === ' ') {
				return this.SelectActions.CloseSelect;
			}
		}
	}

	// return the index of an option from an array of options, based on a search string
	// if the filter is multiple iterations of the same letter (e.g "aaa"), then cycle through first-letter matches
	getIndexByLetter(options: any[], filter: string, startIndex = 0): number {
		const orderedOptions = [
			...options.slice(startIndex),
			...options.slice(0, startIndex),
		];
		const firstMatch = this.filterOptions(orderedOptions, filter)[0];
		const allSameLetter = (array) => array.every((letter) => letter === array[0]);

		// first check if there is an exact match for the typed string
		if (firstMatch) {
			return options.indexOf(firstMatch);
		}
		// if the same letter is being repeated, cycle through first-letter matches
		else if (allSameLetter(filter.split(''))) {
			const matches = this.filterOptions(orderedOptions, filter[0]);
			return options.indexOf(matches[0]);
		}
		// if no matches, return -1
		else {
			return -1;
		}
	}

	// get an updated option index after performing an action
	getUpdatedIndex(currentIndex: number, maxIndex: number, action: number): number {
		const pageSize = 10; // used for pageup/pagedown

		switch (action) {
			case this.SelectActions.First:
				return 0;
			case this.SelectActions.Last:
				return maxIndex;
			case this.SelectActions.Previous:
				return Math.max(0, currentIndex - 1);
			case this.SelectActions.Next:
				return Math.min(maxIndex, currentIndex + 1);
			case this.SelectActions.PageUp:
				return Math.max(0, currentIndex - pageSize);
			case this.SelectActions.PageDown:
				return Math.min(maxIndex, currentIndex + pageSize);
			default:
				return currentIndex;
		}
	}

	// check if an element is currently scrollable
	isScrollable(element: Element): boolean {
		return element && element.clientHeight < element.scrollHeight;
	}

	// ensure a given child element is within the parent's visible scroll area
	// if the child is not visible, scroll the parent
	maintainScrollVisibility(activeElement: HTMLElement, scrollParent: HTMLElement): void {
		const { offsetHeight, offsetTop } = activeElement;
		const { offsetHeight: parentOffsetHeight, scrollTop } = scrollParent;

		const isAbove = offsetTop < scrollTop;
		const isBelow = offsetTop + offsetHeight > scrollTop + parentOffsetHeight;

		if (isAbove) {
			scrollParent.scrollTo(0, offsetTop);
		} else if (isBelow) {
			scrollParent.scrollTo(0, offsetTop - parentOffsetHeight + offsetHeight);
		}
	}

	// #endregion

	// #region Observable methods

	optionsChanged(): void {
		if (this.options?.length > 0) {
			if (!this.isInitizialized) {
				this.init();
			} else {
				this.refreshOptionDivs();
			}
		}
	}

	descriptionsChanged(): void {
		if (this.descriptions?.length > 0) {
			if (!this.isInitizialized) {
				this.init();
			} else {
				this.refreshOptionDivs();
			}
		}
	}

	selectedOptionChanged(): void {
		this.selectedOptionChangedCallback();

		const evt = new CustomEvent('change', {
			bubbles: true
		});

		this.taskQueue.queueMicroTask(() => {
			this.element.dispatchEvent(evt);
		});
	}

	// #endregion

	// #region Utility methods

	/**
	 * Clear option divs (if they exist) and generate new option divs.
	 */
	refreshOptionDivs(): void {
		if (this.options?.length > 0) {
			this.createOptionDivs();
		}
	}

	createOptionDivs(): void {
		this.options.map((option: any, index: number) => {
			const optionDiv = this.createOptionDiv(option, index);
			// prevents duped divs from being added
			const addDiv = (this.optionDivs.length <= 0) || this.optionDivs.every(div => div.id !== optionDiv.id);
			if (addDiv) {
				this.listMenuDiv.appendChild(optionDiv);
			} else {
				optionDiv.remove(); // remove dupes from the DOM
			}
		});
	}

	createOptionDiv(option: any, index: number): HTMLDivElement {
		const optionDiv = document.createElement('div');
		optionDiv.setAttribute('role', 'option');
		optionDiv.id = this.createOptionId(index);
		optionDiv.className =
			index === this.activeIndex ? 'combo-option form-control combo-option-current' : 'combo-option form-control';
		optionDiv.setAttribute('aria-selected', `${index === this.activeIndex}`);
		optionDiv.setAttribute('value', option);
		optionDiv.innerText = this.getOptionDescription(option, index);

		optionDiv.addEventListener('click', (event) => {
			event.stopPropagation();
			this.onOptionClick(index);
		});

		optionDiv.addEventListener('mousedown', this.onOptionMouseDown.bind(this));

		return optionDiv;
	}

	getOptionDescription(option: any, index: number): string {
		if (this.optionPropertyDisplayName) {
			return option[this.optionPropertyDisplayName];
		} else if (this.descriptions.length > 0) {
			return this.descriptions[index];
		} else {
			return option.toString();
		}
	}

	createOptionId(index: number): string {
		return this.optionIdBase + index.toString();
	}

	/**
	 * Set default option, if one exists
	 */
	setDefaultOption() {
		if (!this.isInitizialized) {

			this.defaultIndex = 0;
			if (this.defaultOption) {
				this.defaultIndex = this.options.findIndex(opt => opt === this.defaultOption);
				if (this.defaultIndex === -1) {
					this.defaultIndex = 0;
				}
			}
			this.activeIndex = this.defaultIndex;
		}

		this.onOptionClick(this.activeIndex); // force a default option to be selected
	}

	// #endregion

	// #region Getters/Setters

	get optionDivs(): HTMLDivElement[] {
		return Array.from(this.listMenuDiv.querySelectorAll('[role=option]'));
	}

	get selectedOptionDiv(): HTMLDivElement {
		return this.element.querySelector('#' + this.selectedOptionBoxId) as HTMLDivElement;
	}

	get listMenuDiv(): HTMLDivElement {
		return this.element.querySelector('#' + this.listMenuId) as HTMLDivElement;
	}

	// #endregion
}
